import type { DocOptionRule } from "building-types/options/docOptionRule";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { DocOptionEffect } from "building-types/options/optionEffect";
import type { DocOptionSet, InstanceOptionSet } from "building-types/options/optionSet";
import type { Table } from "common/data/table";
import { sortTableBySortIndex } from "common/utils/sortIndex";
import { formulaOptionHandles } from "doc/formula/formulaConditions";
import { isFormula, parseFormula } from "doc/formula/formulaParsing";
import { getOptionSetArchetypeOrSelf } from "doc/options/getOptionSetArchetypeOrSelf";
import { getOptionChoicesWithDefaults } from "doc/options/optionChoicesTools";
import { groupOptionEffectsByRuleId } from "doc/options/optionTools";
import type { Value } from "expr-eval";

export interface RuleEffect {
  readonly optionSetId: string;
  readonly optionId: string;
  readonly ruleId: string;
  readonly overridable: boolean;
  /** The options referenced in the rule's formula predicate (optionSetId -> optionIds) */
  readonly triggerOptions: Record<string, string[]>;
}

interface EvaluateOptionRuleEffectsInput {
  readonly optionSets: Table<DocOptionSet>;
  readonly instanceOptionSets: Table<InstanceOptionSet>;
  readonly optionRules: Table<DocOptionRule>;
  readonly optionEffects: Table<DocOptionEffect>;
}

interface EvaluationResult {
  readonly active: RuleEffect[];
  readonly skipped: RuleEffect[];
}

/**
 * Evaluates option rules against the given choices and returns the effects that should be applied.
 *
 * @param activeChoices When provided, overridable rules are skipped if none of their trigger options
 * match activeChoices. This prevents out-of-scope overridable rules from overriding in-scope rules
 * for the same optionSetId.
 */
export function evaluateOptionRuleEffects(
  input: EvaluateOptionRuleEffectsInput,
  optionChoices: OptionChoices,
  activeChoices?: OptionChoices,
): EvaluationResult {
  const { optionSets, optionRules, optionEffects } = input;
  const { instanceOptionSets } = input;
  const definitions = { optionSets, instanceOptionSets };

  const active: RuleEffect[] = [];
  const skipped: RuleEffect[] = [];
  const choicesWithDefaults = getOptionChoicesWithDefaults(definitions, optionChoices);

  const optionEffectsByRuleId = groupOptionEffectsByRuleId(optionEffects);

  // Evaluate lowest ranked rules first (higher sortIndex = higher priority, applied last = wins)
  const sortedRules = sortTableBySortIndex(optionRules);

  for (const rule of sortedRules) {
    if (!isFormula(rule.predicate)) {
      continue;
    }

    // Check if predicate references options not in choices (invalid formula)
    const referencedOptions = formulaOptionHandles(rule.predicate.formula);
    let hasInvalidReference = false;
    for (const [optionSetId] of Object.entries(referencedOptions)) {
      if (choicesWithDefaults[optionSetId] === undefined) {
        hasInvalidReference = true;
        break;
      }
    }
    if (hasInvalidReference) {
      continue;
    }

    // For overridable rules, skip if none of the trigger options match activeChoices.
    // This prevents out-of-scope rules from overriding in-scope rules.
    if (rule.overridable && activeChoices !== undefined) {
      let hasTriggerMatch = false;
      for (const [optionSetId, optionIds] of Object.entries(referencedOptions)) {
        for (const optionId of optionIds) {
          if (activeChoices[optionSetId] === optionId) {
            hasTriggerMatch = true;
            break;
          }
        }
        if (hasTriggerMatch) {
          break;
        }
      }
      if (!hasTriggerMatch) {
        continue;
      }
    }

    // Evaluate predicate - formulas are inverted (wrapped in not()), so true means "don't apply"
    try {
      if (parseFormula(rule.predicate.formula).evaluate(choicesWithDefaults as Value)) {
        continue;
      }
    } catch {
      continue;
    }

    for (const effect of optionEffectsByRuleId[rule.id] ?? []) {
      // Skip if effect targets an optionSet that was a trigger (prevents self-referential changes)
      if (referencedOptions[effect.optionSetId] !== undefined) {
        continue;
      }

      // Skip if optionSet doesn't exist
      const optionSet = getOptionSetArchetypeOrSelf(definitions, effect.optionSetId);
      if (optionSet === undefined) {
        continue;
      }

      // Skip if optionSet is disabled
      if (optionSet.disabled) {
        continue;
      }

      // Skip if target option is disabled
      if (optionSet.options[effect.optionId ?? ""]?.disabled) {
        continue;
      }

      // There is only support for enabling options at the moment
      if (effect.effectType === "ENABLE" && effect.optionId) {
        // Later rules (higher sortIndex) override earlier ones
        const existingIndex = active.findIndex((e) => e.optionSetId === effect.optionSetId);
        if (existingIndex !== -1) {
          const skippedEffect = active[existingIndex];
          active.splice(existingIndex, 1);
          skipped.push(skippedEffect);
        }

        active.push({
          optionSetId: effect.optionSetId,
          optionId: effect.optionId,
          ruleId: rule.id,
          overridable: rule.overridable,
          triggerOptions: referencedOptions,
        });
      }
    }
  }

  return { active, skipped };
}

export function ruleEffectsToChoices(effects: readonly RuleEffect[]): OptionChoices {
  return Object.fromEntries(effects.map((effect) => [effect.optionSetId, effect.optionId]));
}
